import type { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ExecutionStatusType } from "build-ts/common";
import { DarwinToolType } from "build-ts/darwin_agent";
import type { ToolExecutionRun } from "./executionStepRun";
import { RESULT_PILL_KIND, TAB_ID, TabId } from "modules/artifacts/dataframe/utils/dataFrameCodeUtils";

// ─── Step helpers ─────────────────────────────────────────────────────────────

/** Returns all step_labels from the last step that has any, filtered and trimmed. */
function stepLabelsInRun(steps: ExecutionStepProto[]): string[] {
  for (let i = steps.length - 1; i >= 0; i--) {
    const labels = (steps[i]!.step_labels ?? []).map(l => l.trim()).filter(Boolean);
    if (labels.length > 0) return labels;
  }
  return [];
}

function analysisOutputCountInRun(steps: ExecutionStepProto[]): number {
  let count = 0;
  for (const s of steps) {
    for (const a of s.artifacts ?? []) {
      if (artifactTypeFromJSON(a.artifact_type) === ArtifactType.ANALYSIS_OUTPUT) count++;
    }
  }
  return count;
}

function hasStatusInRun(steps: ExecutionStepProto[], status: ExecutionStatusType): boolean {
  return steps.some(s => s.status === status);
}

function findDataframeAnalysisInSteps(steps: ExecutionStepProto[]): ArtifactProto | undefined {
  for (let i = steps.length - 1; i >= 0; i--) {
    const a = steps[i]!.artifacts?.find(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS);
    if (a) return a;
  }
  return undefined;
}

function buildArtifactItemLinks(
  labels: string[],
  artifact: ArtifactProto | undefined
): ToolRunLabelParts["artifactItems"] {
  const charts = artifact?.dataframe_analysis?.charts ?? [];
  const reports = artifact?.dataframe_analysis?.reports ?? [];
  return labels.map(label => {
    if (charts.some(c => c.name?.trim() === label))
      return { label, activeTab: TAB_ID.results, subResourceName: label, iconType: RESULT_PILL_KIND.chart };
    if (reports.some(r => r.name?.trim() === label))
      return { label, activeTab: TAB_ID.results, subResourceName: label, iconType: RESULT_PILL_KIND.report };
    return { label, activeTab: TAB_ID.results, subResourceName: label, iconType: RESULT_PILL_KIND.dataframe };
  });
}

// ─── Public type ──────────────────────────────────────────────────────────────

/** Prefix / artifact split for the timeline row UI. */
type ToolRunLabelParts = {
  /** Text before the type icon, or the full label when `artifactHighlight` is null. */
  prefix: string;
  /** Artifact / analysis name shown after the icon; null → no inline icon, use `prefix` as one line. */
  artifactHighlight: string | null;
  /**
   * When a run produces multiple named outputs (e.g. several charts or dataframes from
   * ANALYZE_DATAFRAMES_TOOL), each entry here is individually clickable. When set the
   * row-level click is disabled and individual items are rendered as links instead.
   */
  artifactItems?: {
    label: string;
    activeTab: TabId;
    subResourceName: string;
    iconType: (typeof RESULT_PILL_KIND)[keyof typeof RESULT_PILL_KIND];
  }[];
};

// ─── Label map ────────────────────────────────────────────────────────────────

type LabelArgs = {
  steps: ExecutionStepProto[];
  /** Pre-computed first step label (stepLabelsInRun()[0]); used as artifact name where applicable. */
  name: string | undefined;
  /** Latest step status; only relevant for in-progress labels. */
  status: ExecutionStatusType | undefined;
  /** Number of chart outputs; only provided for ANALYZE_DATAFRAMES_TOOL success labels. */
  chartCount?: number;
  /** Number of dataframe outputs; only provided for ANALYZE_DATAFRAMES_TOOL success labels. */
  dfCount?: number;
  /** Number of report (docx) outputs; only provided for ANALYZE_DATAFRAMES_TOOL success labels. */
  reportCount?: number;
};

type ToolLabelDef = {
  inProgress: (args: LabelArgs) => string;
  /** Returns the prefix string only. `getSuccessLabel` attaches `artifactHighlight` and `artifactItems`. */
  success?: (args: LabelArgs) => string;
  failure: (args: LabelArgs) => string;
};

/**
 * Central label registry keyed by tool type.
 * Each entry owns all three phases (in-progress, success, failure) for that tool.
 */
const TOOL_LABEL_MAP: Partial<Record<DarwinToolType, ToolLabelDef>> = {
  [DarwinToolType.ANALYZE_DATAFRAMES_TOOL]: {
    inProgress: ({ status }) =>
      status === ExecutionStatusType.BUILDING_CHART
        ? "Plotting chart"
        : status === ExecutionStatusType.BUILDING_REPORT
          ? "Generating report"
          : "Writing analysis code",
    success: ({ steps, chartCount = 0, dfCount = 0, reportCount = 0 }) => {
      if (stepLabelsInRun(steps).length === 0) {
        return hasStatusInRun(steps, ExecutionStatusType.BUILDING_CHART) ? "Plotted chart" : "Completed analysis";
      }
      // Pure report(s)
      if (reportCount > 0 && chartCount === 0 && dfCount === 0) {
        return reportCount === 1 ? "Generated report: " : "Generated reports: ";
      }
      // Reports mixed with charts/dataframes — lead with the report label
      if (reportCount > 0 && (chartCount > 0 || dfCount > 0)) {
        const reportWord = reportCount === 1 ? "report" : "reports";
        const chartWord = chartCount === 1 ? "chart" : "charts";
        const dfWord = dfCount === 1 ? "analysis result" : "analysis results";
        const parts = [
          reportCount > 0 ? `generated ${reportWord}` : "",
          chartCount > 0 ? `plotted ${chartWord}` : "",
          dfCount > 0 ? dfWord : "",
        ].filter(Boolean);
        return parts.join(" & ").replace(/^./, c => c.toUpperCase()) + ": ";
      }
      if (chartCount > 0 && dfCount > 0) {
        const chartWord = chartCount === 1 ? "chart" : "charts";
        const dfWord = dfCount === 1 ? "completed analysis result" : "completed analysis results";
        return `Plotted ${chartWord} & ${dfWord}: `;
      }
      if (chartCount > 0) return chartCount === 1 ? "Plotted chart: " : "Plotted charts: ";
      return dfCount === 1 ? "Completed analysis. Result: " : "Completed analysis. Results: ";
    },
    failure: ({ steps }) =>
      hasStatusInRun(steps, ExecutionStatusType.BUILDING_CHART)
        ? "Chart generation encountered an issue"
        : "Code execution encountered an issue",
  },
  [DarwinToolType.DATAFRAME_TOOL]: {
    inProgress: () => "Querying source data",
    success: ({ name }) => (name ? "Completed query: " : "Completed query"),
    failure: () => "Source query encountered an issue",
  },
  [DarwinToolType.SKILL_TOOL]: {
    inProgress: ({ status }) => {
      switch (status) {
        case ExecutionStatusType.EXECUTING_TOOL_CALL:
        case ExecutionStatusType.VALIDATING_TOOL_CALL:
          return "Starting skill";
        case ExecutionStatusType.LOADING_SCENARIO_MANAGEMENT_SKILL:
          return "Loading scenario management skill";
        case ExecutionStatusType.LOADING_SCENARIO_SYNTHESIS_SKILL:
          return "Loading scenario synthesis skill";
        default:
          return "Running skill";
      }
    },
    success: () => "Loaded skill",
    failure: () => "Loading skill encountered an issue",
  },
  [DarwinToolType.GET_SCENARIO_DETAIL_TOOL]: {
    inProgress: () => "Loading workflow",
    success: ({ name }) => (name ? "Loaded workflow: " : "Loaded workflow"),
    failure: () => "Loading workflow encountered an issue",
  },
  [DarwinToolType.UPDATE_SCENARIO_METADATA_TOOL]: {
    inProgress: ({ steps }) => {
      const isCreate = hasStatusInRun(steps, ExecutionStatusType.CREATING_SCENARIO_METADATA);
      return isCreate ? "Creating workflow" : "Updating workflow";
    },
    success: ({ steps, name }) => {
      const isCreate = hasStatusInRun(steps, ExecutionStatusType.CREATING_SCENARIO_METADATA);
      const pastVerb = isCreate ? "Created" : "Updated";
      return name ? `${pastVerb} workflow: ` : `${pastVerb} workflow`;
    },
    failure: ({ steps }) => {
      const isCreate = hasStatusInRun(steps, ExecutionStatusType.CREATING_SCENARIO_METADATA);
      return isCreate ? "Workflow creation encountered an issue" : "Workflow update encountered an issue";
    },
  },
  [DarwinToolType.SAVE_SCENARIO_TOOL]: {
    inProgress: () => "Saving workflow",
    success: ({ name }) => (name ? "Saved workflow: " : "Saved workflow"),
    failure: () => "Saving workflow encountered an issue",
  },
  [DarwinToolType.KG_OBSERVATION_DATA]: {
    inProgress: ({ status }) => {
      switch (status) {
        case ExecutionStatusType.BUILDING_KG_OBSERVATION:
          return "Building observation";
        case ExecutionStatusType.EXECUTING_KG_OBSERVATION:
          return "Running observation";
        default:
          return "Fetching observations";
      }
    },
    success: () => "Explored observation",
    failure: () => "Query encountered an issue",
  },
  [DarwinToolType.LOAD_SKILL_TOOL]: {
    inProgress: ({ name }) => (name ? `Loading ${name} skill` : "Loading skill"),
    success: ({ name }) => (name ? "Loaded skill: " : "Loaded skill"),
    failure: ({ name }) => (name ? `Loading ${name} skill encountered an issue` : "Loading skill encountered an issue"),
  },
  [DarwinToolType.GET_PAST_JOB_RUNS]: {
    inProgress: () => "Fetching workflow runs",
    success: ({ steps, name }) => {
      const n = analysisOutputCountInRun(steps);
      const countStr = `${n} run${n === 1 ? "" : "s"}`;
      return name ? `Fetched ${countStr} for ` : `Fetched ${countStr}`;
    },
    failure: () => "Workflow runs fetch encountered an issue",
  },
  [DarwinToolType.BUILD_ANALYSIS_OUTPUT_TOOL]: {
    inProgress: () => "Saving workflow output",
    success: ({ name }) => (name ? "Saved output for workflow: " : "Saved workflow output"),
    failure: () => "Workflow output saving encountered an issue",
  },
  [DarwinToolType.AGENT_TOOL]: {
    inProgress: () => "Running sub-agent",
    success: () => "Sub-agent finished",
    failure: () => "Sub-agent execution encountered an issue",
  },
};

// ─── Phase resolvers ──────────────────────────────────────────────────────────

function getInProgressLabel(run: ToolExecutionRun): string {
  const { toolType, steps } = run;
  if (toolType === undefined) return "Unrecognized tool";
  const name = stepLabelsInRun(steps)[0];
  const status = steps[steps.length - 1]?.status;
  return TOOL_LABEL_MAP[toolType]?.inProgress({ steps, name, status }) ?? "Unrecognized tool";
}

function getSuccessLabel(run: ToolExecutionRun): ToolRunLabelParts {
  const { toolType, steps } = run;

  // ANALYZE_DATAFRAMES_TOOL: detect per-label whether each output is a chart or
  // a dataframe by looking it up in the artifact's charts/pandas_dataframes arrays.
  if (toolType === DarwinToolType.ANALYZE_DATAFRAMES_TOOL) {
    const labels = stepLabelsInRun(steps);
    const dfArtifact = findDataframeAnalysisInSteps(steps);
    const successFn = TOOL_LABEL_MAP[toolType]!.success!;

    if (labels.length > 0) {
      const items = buildArtifactItemLinks(labels, dfArtifact);
      const chartCount = items!.filter(i => i.iconType === RESULT_PILL_KIND.chart).length;
      const dfCount = items!.filter(i => i.iconType === RESULT_PILL_KIND.dataframe).length;
      const reportCount = items!.filter(i => i.iconType === RESULT_PILL_KIND.report).length;
      const prefix = successFn({ steps, name: labels[0], status: undefined, chartCount, dfCount, reportCount });
      return { prefix, artifactHighlight: labels.join(", "), artifactItems: items };
    }

    return { prefix: successFn({ steps, name: undefined, status: undefined }), artifactHighlight: null };
  }

  if (toolType === undefined) return { prefix: "Tool finished", artifactHighlight: null };
  const name = stepLabelsInRun(steps)[0];
  const prefix = TOOL_LABEL_MAP[toolType]?.success?.({ steps, name, status: undefined }) ?? "Tool finished";
  return { prefix, artifactHighlight: name ?? null };
}

function getFailureLabel(run: ToolExecutionRun): string {
  const { toolType, steps } = run;
  if (toolType === undefined) return "Tool failed";
  const name = stepLabelsInRun(steps)[0];
  return TOOL_LABEL_MAP[toolType]?.failure({ steps, name, status: undefined }) ?? "Tool failed";
}

// ─── Public entry point ───────────────────────────────────────────────────────

/** Prefix / artifact split for UI (icon sits between them when `artifactHighlight` is set). */
export function toolRunLabelParts(run: ToolExecutionRun): ToolRunLabelParts {
  if (run.failed) {
    return { prefix: getFailureLabel(run), artifactHighlight: null };
  }
  if (run.completed) {
    return getSuccessLabel(run);
  }
  return { prefix: getInProgressLabel(run), artifactHighlight: null };
}
